import * as React from "react";
import {styled} from "styled-components";
import {colorPalette} from "../styles/colorPalette";
import {FlexCenterStyle, baseDistance, fontSize, fontWeight, radius, zIndex} from "../styles/styles";
import {SVGIcon} from "../../widgets/button/SVGIcon";
import {ResizeDetector} from "../../../utils/resize/ResizeDetector";
import {IconButtonV5} from "../interaction/IconButtonV5";
import XmarkIcon from "../icons/xmark.svg?react";

interface IShortcutWindowV5Props {
	readonly onClose: () => void;
}

interface IShortcutDescriptionV5 {
	description: string;
	keys: string[];
}

const os = window.navigator.userAgent;

const altOrOption = os.match(/Mac/i) ? "Option" : "Alt";
const ctrlOrControl = os.match(/Mac/i) ? "Control" : "Ctrl";

const shortcuts: IShortcutDescriptionV5[] = [
	{description: "Disable snap to grid and angle", keys: [altOrOption, "Drag"]},
	{description: "Hide captions", keys: ["C"]},
	{description: "Move the boundary line (edit mode)", keys: [ctrlOrControl, "Drag"]},
	{description: "Pan around the space", keys: ["Right click", "Drag"]},
	{description: "Pan around the space", keys: ["Middle click", "Drag"]},
	{description: "Pan around the space", keys: ["Space bar", "Drag"]},
	{description: "Position Lock", keys: ["Shift", "L"]},
	{description: "Position Unlock", keys: [altOrOption, "Shift", "L"]},
	{description: "Stamp selection", keys: [ctrlOrControl, "S"]},
	{description: "Vertically move a Xyicon", keys: ["Z", "Drag"]},
	{description: "View in 3D", keys: ["Shift", "Drag"]},
];

export class ShortcutWindowV5 extends React.Component<IShortcutWindowV5Props> {
	private _shortcutWindowRef = React.createRef<HTMLDivElement>();
	private _dragHandleRef = React.createRef<HTMLDivElement>();
	private _initPosX: number = 0; // initial position of the drag-handle when popup is opened
	private _initPosY: number = 0;
	private _actualPosX: number = 0; // saved when the user moves the popup
	private _actualPosY: number = 0;
	private _offsetX: number = 0; // resize width
	private _offsetY: number = 0;
	private _isDragIconGrabbed = false;
	private _resizeDetector: ResizeDetector;

	private onGrabDragHandle = () => {
		this._isDragIconGrabbed = true;

		// if the page was resized, the initPos needs to be updated
		this._initPosX -= this._offsetX;
		this._initPosY -= this._offsetY;
		this._offsetX = 0;
		this._offsetY = 0;
	};

	private onReleaseDragHandle = () => {
		if (this._isDragIconGrabbed) {
			this._isDragIconGrabbed = false;
		}
	};

	private onMoveWindow = (e: MouseEvent) => {
		if (this._isDragIconGrabbed) {
			this._shortcutWindowRef.current.style.transform = `translate(calc(${e.clientX - (this._initPosX - this._offsetX)}px - 50%), calc(${e.clientY - (this._initPosY - this._offsetY)}px - 50%))`;

			// save the actual position to calculate the difference when page is resized
			this._actualPosX = e.clientX;
			this._actualPosY = e.clientY;
		}
	};

	private calculateInitPosition = () => {
		const bbox = this._dragHandleRef.current.getBoundingClientRect();

		this._initPosX = (bbox.left + bbox.right) / 2;
		this._initPosY = (bbox.top + bbox.bottom) / 2;
	};

	private calculateOffset = () => {
		const bbox = this._dragHandleRef.current.getBoundingClientRect();
		const x = (bbox.left + bbox.right) / 2;
		const y = (bbox.top + bbox.bottom) / 2;

		// the amount of resize can be calculated from the difference of the last moved position (actualPos) and the position after resize (x,y)
		this._offsetX = this._actualPosX - x;
		this._offsetY = this._actualPosY - y;
	};

	public override componentDidMount(): void {
		document.addEventListener("mouseup", this.onReleaseDragHandle);
		document.addEventListener("mousemove", this.onMoveWindow);

		this.calculateInitPosition();

		this._resizeDetector = new ResizeDetector(document.body);
		this._resizeDetector.resize.add(this.calculateOffset, this);
	}

	public override componentWillUnmount(): void {
		document.removeEventListener("mouseup", this.onReleaseDragHandle);
		document.removeEventListener("mousemove", this.onMoveWindow);

		this._resizeDetector.resize.remove(this.calculateOffset, this);
		this._resizeDetector.dispose();
	}

	public override render() {
		return (
			<ShortcutWindowStyled ref={this._shortcutWindowRef}>
				<ShortcutWindowTitleStyled>
					<div
						ref={this._dragHandleRef}
						className="draghandle hbox alignCenter"
						onMouseDown={this.onGrabDragHandle}
					>
						<SVGIcon
							icon="drag"
							classNames="drag-icon"
						/>
					</div>
					<TitleContentStyled>Xyicon Keyboard Shortcuts</TitleContentStyled>
					<IconButtonV5
						IconComponent={XmarkIcon}
						onClick={() => this.props.onClose()}
					/>
				</ShortcutWindowTitleStyled>
				<ShortcutWindowMessageStyled>
					{shortcuts.map((shortcut) => {
						return (
							<ShortcutStyled key={`${shortcut.description}_${shortcut.keys.join(",")}`}>
								<span>{shortcut.description}</span>
								<KeysStyled>
									{shortcut.keys.map((key, index) => (
										<>
											<KeyStyled key={`${key}-${index}`}>{key}</KeyStyled>
											{index < shortcut.keys.length - 1 && <PlusStyled key={`plus-${index}`}>+</PlusStyled>}
										</>
									))}
								</KeysStyled>
							</ShortcutStyled>
						);
					})}
				</ShortcutWindowMessageStyled>
			</ShortcutWindowStyled>
		);
	}
}

const ShortcutWindowStyled = styled.div`
	background: ${colorPalette.white};
	box-shadow:
		0px 16px 16px rgba(50, 50, 71, 0.08),
		0px 24px 32px rgba(50, 50, 71, 0.08);
	max-width: 500px;
	width: 440px;
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	z-index: ${zIndex.popup};
	border-radius: ${radius.md};
`;

const ShortcutWindowTitleStyled = styled.div`
	height: 24px;
	padding: ${baseDistance.sm};
	font-size: ${fontSize.md};
	font-weight: ${fontWeight.normal};
	justify-content: space-between;
	${FlexCenterStyle}

	.draghandle {
		cursor: pointer;

		svg {
			width: 14px;
			height: 14px;
			fill: ${colorPalette.gray.c400};
			margin: 0 ${baseDistance.xs};
			margin-right: 10px;
		}
	}

	svg {
		cursor: pointer;

		path {
			width: 14px;
			height: 14px;
			fill: ${colorPalette.gray.c400};
			margin: 0 ${baseDistance.xs};
		}
	}
`;

const TitleContentStyled = styled.div`
	width: 400px;
	font-size: ${fontSize.xl};
	font-weight: ${fontWeight.bold};
	line-height: 18px;
`;

const ShortcutWindowMessageStyled = styled.div`
	padding: 0px ${baseDistance.sm};
	font-size: ${fontSize.md};
`;

const ShortcutStyled = styled.div`
	height: 24px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin: ${baseDistance.sm} 0;
`;

const KeysStyled = styled.div`
	display: flex;
	align-items: center;
`;

const KeyStyled = styled.div`
	background-color: ${colorPalette.gray.c950};
	color: ${colorPalette.white};
	padding: 5px;
	margin: 0 1px;
	border-radius: ${radius.sm};
	font-size: ${fontSize.sm};
	font-weight: ${fontWeight.semiBold};
`;

const PlusStyled = styled.div`
	color: ${colorPalette.gray.c700Dark};
	padding: ${baseDistance.xs};
	font-size: ${fontSize.sm};
	font-weight: ${fontWeight.semiBold};
`;
